import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry error monitoring.
 *
 * Set VITE_SENTRY_DSN in your environment variables.
 * Sentry will not initialize if the DSN is missing (safe for local dev).
 */
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

export function initSentry() {
  if (!SENTRY_DSN) {
    console.warn('[Sentry] No DSN found. Set VITE_SENTRY_DSN in your .env file.');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE, // 'development' | 'production'
    release: import.meta.env.VITE_APP_VERSION || '1.0.0',

    // --- Performance Monitoring ---
    // Capture 20% of transactions in production (adjust as needed)
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,

    // --- Session Replay (captures what the user saw before the crash) ---
    replaysSessionSampleRate: 0.0,   // Don't record normal sessions
    replaysOnErrorSampleRate: 1.0,   // Always record when an error occurs

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Privacy: mask all text and block all media by default
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Filter out noisy, non-actionable errors
    beforeSend(event) {
      const exceptionValue = event.exception?.values?.[0]?.value || '';
      const exceptionType = event.exception?.values?.[0]?.type || '';
      const searchText = `${exceptionType} ${exceptionValue}`;

      // Ignore common browser extension / network noise
      const ignorePatterns = [
        'ResizeObserver loop',
        'Non-Error promise rejection',
        'Network request failed',
        'AbortError',
        'signal is aborted without reason',
        // ChunkLoadError intentionally NOT ignored — we need to track these
        // since they can cause blank screens after deploys or in incognito
      ];

      if (ignorePatterns.some((p) => searchText.includes(p))) {
        return null; // Drop this event
      }

      return event;
    },
  });
}

// Re-export Sentry for use in other files
export { Sentry };
